import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import { useEffect, useState } from 'react';
import type MollieService from 'app/services/mollie';

interface MollieComponentInstance {
  mount: (element: HTMLElement | string) => void;
  unmount: () => void;
  addEventListener: (event: string, callback: (event: MollieEvent) => void) => void;
}

interface MollieEvent {
  error?: string;
  touched?: boolean;
  dirty?: boolean;
}

interface MollieStyle {
  styles: {
    base: Record<string, string>;
  };
}

interface MollieHookReturn {
  isLoaded: boolean;
  load: MollieService['load'];
  hasError: boolean;
  createComponent: (id: string, style?: MollieStyle) => MollieComponentInstance;
  createToken: () => Promise<{ token: string; error: string }>;
}

export const useMollie = (): MollieHookReturn => {
  const mollie = useEmberService('mollie');

  const [isLoaded, setIsLoaded] = useState<boolean>(mollie.isLoaded);
  const [hasError, setHasError] = useState<boolean>(mollie.hasError);

  useEffect(() => {
    const onChange = () => {
      setIsLoaded(mollie.isLoaded);
      setHasError(mollie.hasError);
    };
    mollie.addObserver('instance', onChange);
    mollie.addObserver('hasError', onChange);
    return () => {
      mollie.removeObserver('instance', onChange);
      mollie.removeObserver('hasError', onChange);
    };
  }, [mollie]);

  return {
    isLoaded,
    load: mollie.load.bind(mollie),
    hasError,
    createComponent: mollie.createComponent as MollieHookReturn['createComponent'],
    createToken: mollie.createToken as MollieHookReturn['createToken'],
  };
};
